import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Gamepad2, RotateCcw, Download, Copy, Check } from "lucide-react";
import { useState } from "react";

export default function ChessEngine() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  if (!user) return <Navigate to="/login" replace />;
  
  const [fen, setFen] = useState("rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1");
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const handleAnalyze = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/chess/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fen, depth: 20 }),
      });
      const data = await res.json();
      setAnalysis(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };
  
  const handleNewGame = () => {
    setFen("rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1");
    setAnalysis(null);
  };
  
  const handleCopyFen = () => {
    navigator.clipboard.writeText(fen);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Gamepad2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Chess Engine</h1>
              <p className="text-slate-400 text-sm">Stockfish on Linux</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleNewGame}>
              <RotateCcw className="h-4 w-4 mr-2" />
              New Game
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-slate-800 rounded-xl p-6">
            <div className="aspect-square max-w-lg mx-auto bg-slate-700 rounded-lg flex items-center justify-center">
              <div className="text-center text-slate-400">
                <Gamepad2 className="h-24 w-24 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Chess Board</p>
                <p className="text-sm opacity-60">Position from FEN</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-slate-800 rounded-xl p-6">
              <Label className="text-slate-300 text-sm mb-2 block">FEN Position</Label>
              <div className="flex gap-2">
                <Input
                  value={fen}
                  onChange={(e) => setFen(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white font-mono text-sm"
                />
                <Button variant="ghost" size="icon" onClick={handleCopyFen}>
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              
              <Button 
                onClick={handleAnalyze} 
                disabled={loading}
                className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600"
              >
                {loading ? "Analyzing..." : "Analyze Position"}
              </Button>
            </div>
            
            {analysis && (
              <div className="bg-slate-800 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">Analysis Results</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Best Move:</span>
                    <span className="text-white font-mono">{analysis.best_move}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Score:</span>
                    <span className={`font-mono ${analysis.score > 0 ? "text-green-400" : "text-red-400"}`}>
                      {analysis.score > 0 ? `+${analysis.score}` : analysis.score}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Depth:</span>
                    <span className="text-white">{analysis.depth}</span>
                  </div>
                  {analysis.pv && (
                    <div>
                      <span className="text-slate-400 text-sm">Principal Variation:</span>
                      <p className="text-white font-mono text-sm mt-1">{analysis.pv}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="bg-slate-800 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-3">Engine Info</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Engine:</span>
                  <span className="text-white">Stockfish 16</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Platform:</span>
                  <span className="text-white">Linux (cloud)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Hash:</span>
                  <span className="text-white">256 MB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Threads:</span>
                  <span className="text-white">4</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}